import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Brain, Sparkles, Target, CheckCircle2, Users, Zap, ArrowRight, Menu, X, MessageSquare, Check, Star, Rocket, Building2 } from 'lucide-react';
import UserbackWidget from '../components/UserbackWidget';
import BookDemoModal from '../components/BookDemoModal';
import ContactModal from '../components/ContactModal';

const screenshots = [
  {
    src: '/screenshots/chat_interface_zoomed.png',
    alt: 'Chat Interface',
    title: 'Interactive Chat',
  },
  {
    src: '/screenshots/sims_directory_main.png',
    alt: 'Simulations Directory',
    title: 'Browse Simulations',
  },
];

function StackedScreenshots() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % screenshots.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full aspect-video mb-4">
      <div className="relative w-full h-full overflow-hidden">
        {screenshots.map((screenshot, index) => {
          const isActive = index === activeIndex;
          
          return (
            <div
              key={screenshot.src}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              style={{
                zIndex: isActive ? 2 : 1,
              }}
            >
              <div 
                className="relative rounded-2xl overflow-hidden bg-white p-6 h-full"
                style={{
                  boxShadow: '0 25px 70px rgba(0, 0, 0, 0.35), 0 15px 30px rgba(0, 0, 0, 0.25)',
                }}
              >
                <div className="relative rounded-lg overflow-hidden border-2 border-slate-200 h-full">
                  <img 
                    src={screenshot.src} 
                    alt={screenshot.alt}
                    className={`w-full h-full bg-white ${
                      index === 1 ? 'object-cover scale-150' : 'object-contain'
                    }`}
                  />
                </div>
                {isActive && (
                  <div className="absolute bottom-8 left-8 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <p className="text-white text-sm font-medium">{screenshot.title}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Navigation Dots */}
      <div className="flex gap-2 justify-center mt-4">
        {screenshots.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === activeIndex 
                ? 'bg-emerald-400 w-8' 
                : 'bg-slate-400/50 hover:bg-slate-300'
            }`}
            aria-label={`View screenshot ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showBookDemoModal, setShowBookDemoModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleBookDemo = () => {
    setShowBookDemoModal(true);
  };

  const handleCloseModal = () => {
    setShowBookDemoModal(false);
  };

  const handleContact = () => {
    setShowContactModal(true);
  };

  const handleCloseContactModal = () => {
    setShowContactModal(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
        scrollY > 50 ? 'bg-slate-950/80 backdrop-blur-lg border-slate-800' : 'bg-transparent border-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight">Axone</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('how-it-works')} className="text-slate-300 hover:text-emerald-400 transition-colors duration-200">How It Works</button>
            <button onClick={() => scrollToSection('features')} className="text-slate-300 hover:text-emerald-400 transition-colors duration-200">Features</button>
            <button onClick={() => scrollToSection('pricing')} className="text-slate-300 hover:text-emerald-400 transition-colors duration-200">Pricing</button>
            <button onClick={() => scrollToSection('why-axone')} className="text-slate-300 hover:text-emerald-400 transition-colors duration-200">Why Axone</button>
            <button onClick={() => scrollToSection('testimonials')} className="text-slate-300 hover:text-emerald-400 transition-colors duration-200">Testimonials</button>
            <Button onClick={() => navigate('/login')} variant="outline" className="border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-white transition-all duration-200">
              Sign In
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-white">
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800">
            <nav className="flex flex-col px-6 py-4 space-y-4">
              <button onClick={() => scrollToSection('how-it-works')} className="text-left text-slate-300 hover:text-emerald-400 transition-colors duration-200">How It Works</button>
              <button onClick={() => scrollToSection('features')} className="text-left text-slate-300 hover:text-emerald-400 transition-colors duration-200">Features</button>
              <button onClick={() => scrollToSection('pricing')} className="text-left text-slate-300 hover:text-emerald-400 transition-colors duration-200">Pricing</button>
              <button onClick={() => scrollToSection('why-axone')} className="text-left text-slate-300 hover:text-emerald-400 transition-colors duration-200">Why Axone</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-left text-slate-300 hover:text-emerald-400 transition-colors duration-200">Testimonials</button>
              <Button onClick={() => navigate('/login')} variant="outline" className="border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-white transition-all duration-200 w-full">
                Sign In
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950/80 to-slate-950 z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1523961131990-5ea7c61b2107" 
            alt="" 
            className="w-full h-full object-cover opacity-30"
            style={{
              transform: `translateY(${scrollY * 0.5}px)`
            }}
          />
        </div>

        {/* Floating Orbs */}
        <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
          <div 
            className="absolute w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"
            style={{
              top: '20%',
              left: '10%',
              transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
              transition: 'transform 0.3s ease-out'
            }}
          ></div>
          <div 
            className="absolute w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl"
            style={{
              bottom: '20%',
              right: '10%',
              transform: `translate(${mousePosition.x * -0.015}px, ${mousePosition.y * -0.015}px)`,
              transition: 'transform 0.3s ease-out'
            }}
          ></div>
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Hero Content */}
            <div className="animate-fade-in-up text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-8 hover:bg-emerald-500/20 transition-all duration-300">
                <Sparkles className="w-4 h-4 text-emerald-400 animate-pulse" />
                <span className="text-sm text-emerald-400 font-medium">Powered by Advanced AI</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 leading-[1.15] tracking-[-0.02em]">
                <span className="block mb-3">Unlock True Potential</span>
                <span className="block bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent font-normal">with AI-Powered</span>
                <span className="block bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent font-normal">Skill Assessments</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 mb-8 font-light leading-relaxed">
                Experience a new era of skills-based evaluation — where every conversation, challenge, and scenario is powered by intelligent AI. Whether you're screening candidates, running assessment centres for promotions, or developing future leaders, our platform transforms evaluations into immersive, data-driven experiences.
                <br />
                <br />
                <span className="text-emerald-400 font-medium">Smart. Scalable. Skills-first.</span>
              </p>
            </div>

            {/* Right Column - Experience Axone Screenshots */}
            <div className="relative">
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl shadow-2xl p-6 md:p-8 border border-white/10">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-3 text-white">
                    Experience Axone
                  </h2>
                  <p className="text-base text-slate-300">
                    See how our platform transforms skills assessment
                  </p>
                </div>

                {/* Stacked Cards with Rotation Animation */}
                <StackedScreenshots />

                {/* Stats Section - Always Visible */}
                <div className="grid grid-cols-3 gap-4 mt-6 relative z-20">
                  <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-200 shadow-sm">
                    <div className="text-2xl font-bold text-emerald-600 mb-1">22+</div>
                    <div className="text-xs text-slate-700 font-medium">Simulations</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200 shadow-sm">
                    <div className="text-2xl font-bold text-blue-600 mb-1">8+</div>
                    <div className="text-xs text-slate-700 font-medium">AI Agents</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-200 shadow-sm">
                    <div className="text-2xl font-bold text-purple-600 mb-1">Real-time</div>
                    <div className="text-xs text-slate-700 font-medium">Evaluation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button - Centered Below */}
          <div className="flex items-center justify-center mt-12">
            <Button 
              size="lg" 
              className="relative bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-110 shadow-lg shadow-emerald-500/50 hover:shadow-2xl hover:shadow-emerald-400/60 group animate-pulse-subtle overflow-hidden"
              onClick={handleBookDemo}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></span>
              <span className="relative z-10 flex items-center">
                Try demo simulation
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Button>
          </div>
        </div>

      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light tracking-tight mb-6 text-white">How It Works</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">Three simple steps to transform your hiring process</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Step 1 */}
            <div 
              className="group relative"
              onMouseEnter={() => setHoveredCard('step1')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className={`absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl transition-opacity duration-300 blur ${hoveredCard === 'step1' ? 'opacity-100' : 'opacity-0'}`}></div>
              <Card className="relative h-full bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all duration-300 p-8 transform hover:scale-105 hover:-translate-y-2">
                <CardContent className="p-0 flex flex-col h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform duration-300">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-emerald-400 font-semibold mb-2 text-sm tracking-wider uppercase">Step 01</div>
                  <h3 className="text-2xl font-semibold mb-4 text-white min-h-[4rem]">Select a simulation from our library or create your own</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Choose from our curated library of pre-built simulations or design custom scenario-based assessments tailored to your specific needs.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Step 2 */}
            <div 
              className="group relative"
              onMouseEnter={() => setHoveredCard('step2')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className={`absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl transition-opacity duration-300 blur ${hoveredCard === 'step2' ? 'opacity-100' : 'opacity-0'}`}></div>
              <Card className="relative h-full bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all duration-300 p-8 transform hover:scale-105 hover:-translate-y-2">
                <CardContent className="p-0 flex flex-col h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform duration-300">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-emerald-400 font-semibold mb-2 text-sm tracking-wider uppercase">Step 02</div>
                  <h3 className="text-2xl font-semibold mb-4 text-white min-h-[4rem]">Evaluate Responses</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Candidates engage with intelligent, adaptive simulations that evolve based on their decisions and actions.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Step 3 */}
            <div 
              className="group relative"
              onMouseEnter={() => setHoveredCard('step3')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className={`absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl transition-opacity duration-300 blur ${hoveredCard === 'step3' ? 'opacity-100' : 'opacity-0'}`}></div>
              <Card className="relative h-full bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all duration-300 p-8 transform hover:scale-105 hover:-translate-y-2">
                <CardContent className="p-0 flex flex-col h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform duration-300">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-emerald-400 font-semibold mb-2 text-sm tracking-wider uppercase">Step 03</div>
                  <h3 className="text-2xl font-semibold mb-4 text-white min-h-[4rem]">Get Intelligent Insights</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Receive comprehensive AI-powered analysis with detailed skill assessments and actionable recommendations.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section with Animation */}
      <section className="py-20 px-6 relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-emerald-600/5"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group cursor-pointer">
              <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent mb-3 transform group-hover:scale-105 transition-transform duration-300">
                Unmatched Hiring Precision
              </div>
              <div className="text-slate-600 text-base leading-relaxed">Delivering far greater accuracy than traditional methods</div>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent mb-3 transform group-hover:scale-105 transition-transform duration-300">
                Faster Talent Decisions
              </div>
              <div className="text-slate-600 text-base leading-relaxed">Streamlining evaluation to save significant time and effort</div>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent mb-3 transform group-hover:scale-105 transition-transform duration-300">
                Proven at Scale
              </div>
              <div className="text-slate-600 text-base leading-relaxed">Thousands of simulations powering smarter hiring across industries</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-6 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white opacity-50"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-light tracking-tight mb-6 text-slate-900">Features</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">Explore what Axone offers to help your team scale</p>
          </div>

          <div className="space-y-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div 
                className="order-2 lg:order-1"
                onMouseEnter={() => setHoveredCard('screen1')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-emerald-300">
                  <img 
                    src="/screenshots/sims_directory.png" 
                    alt="Simulations Directory"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-4">
                  <span className="text-sm text-emerald-600 font-medium">Discover</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Extensive Simulation Library
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Choose from 20+ professionally crafted scenarios across leadership, sales, customer success, and technical roles. Filter by difficulty, role, skills, and tags to find the perfect assessment.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Advanced filtering and search capabilities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Tag-based categorization for easy discovery</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Detailed skill evaluation breakdowns</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-4">
                  <span className="text-sm text-emerald-600 font-medium">Engage</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Intuitive Chat Interface
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Interact with AI agents in a familiar, professional chat environment. Real-time conversations with multiple stakeholders, complete with profile pictures, typing indicators, and dynamic objectives tracking.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Natural conversation flow with AI agents</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Live objectives tracking with progress bars</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Professional three-column layout</span>
                  </li>
                </ul>
              </div>
              <div 
                onMouseEnter={() => setHoveredCard('screen2')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-blue-300">
                  <img 
                    src="/screenshots/sim_chat_interface.png" 
                    alt="Chat Interface"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div 
                className="order-2 lg:order-1"
                onMouseEnter={() => setHoveredCard('screen3')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-purple-300">
                  <img 
                    src="/screenshots/sim_evaluation_overview.png" 
                    alt="Evaluation Dashboard"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-4">
                  <span className="text-sm text-emerald-600 font-medium">Analyze</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Rich Data Visualizations
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Transform candidate performance into actionable insights. Radar charts, bar graphs, line charts, and detailed feedback tables provide a complete picture of each candidate's strengths and development areas.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Skills radar charts for visual comparison</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Performance tracking over time</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Detailed skill-by-skill feedback</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-4">
                  <span className="text-sm text-emerald-600 font-medium">Compare</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Side-by-Side Comparison
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Compare up to 4 candidates simultaneously. Overlay radar charts, highlight top performers per metric, and make data-driven hiring decisions with confidence.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Multi-candidate skill comparison</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Best performer highlighting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Export comparison reports</span>
                  </li>
                </ul>
              </div>
              <div 
                onMouseEnter={() => setHoveredCard('screen4')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-orange-300">
                  <img 
                    src="/screenshots/result_comparison.png" 
                    alt="Comparison View"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div 
                className="order-2 lg:order-1"
                onMouseEnter={() => setHoveredCard('screen5')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-teal-300">
                  <img 
                    src="/screenshots/sim_results_all_candidates.png" 
                    alt="Dashboard Overview"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-4">
                  <span className="text-sm text-emerald-600 font-medium">Manage</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Comprehensive Dashboard
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Monitor all candidates, track completion rates, view top performers, and manage results all in one place. Add annotations, tags, and notes to streamline your hiring workflow.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Real-time candidate status tracking</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Annotation and tagging system</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">Performance analytics and insights</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Axone Section */}
      <section id="why-axone" className="py-32 px-6 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="absolute inset-0 opacity-5">
          <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995" alt="" className="w-full h-full object-cover" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light tracking-tight mb-6 text-white">Why Axone?</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">The future of hiring is intelligent, realistic, and fair</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Feature 1 */}
            <div 
              className="group relative"
              onMouseEnter={() => setHoveredCard('feature1')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm hover:bg-slate-800/70 transition-all duration-300 p-8 h-full transform hover:scale-105 cursor-pointer">
                <CardContent className="p-0">
                  <Zap className={`w-12 h-12 text-emerald-400 mb-4 transition-all duration-300 ${hoveredCard === 'feature1' ? 'scale-125 rotate-12' : ''}`} />
                  <h3 className="text-2xl font-semibold mb-4 text-white">Powered by Cutting-Edge LLMs</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Our simulations leverage the latest advances in AI to create dynamic, context-aware scenarios that adapt to each candidate's responses in real-time.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Feature 2 */}
            <div 
              className="group relative"
              onMouseEnter={() => setHoveredCard('feature2')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm hover:bg-slate-800/70 transition-all duration-300 p-8 h-full transform hover:scale-105 cursor-pointer">
                <CardContent className="p-0">
                  <CheckCircle2 className={`w-12 h-12 text-emerald-400 mb-4 transition-all duration-300 ${hoveredCard === 'feature2' ? 'scale-125 rotate-12' : ''}`} />
                  <h3 className="text-2xl font-semibold mb-4 text-white">Unmatched Realism</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Move beyond traditional assessments. Our simulations mirror real workplace challenges, revealing how candidates actually perform under pressure.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Feature 3 */}
            <div 
              className="group relative"
              onMouseEnter={() => setHoveredCard('feature3')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm hover:bg-slate-800/70 transition-all duration-300 p-8 h-full transform hover:scale-105 cursor-pointer">
                <CardContent className="p-0">
                  <Target className={`w-12 h-12 text-emerald-400 mb-4 transition-all duration-300 ${hoveredCard === 'feature3' ? 'scale-125 rotate-12' : ''}`} />
                  <h3 className="text-2xl font-semibold mb-4 text-white">Precision Evaluation</h3>
                  <p className="text-slate-300 leading-relaxed">
                    AI-driven analysis goes beyond scores. Get deep insights into decision-making patterns, problem-solving approaches, and skill gaps.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Feature 4 */}
            <div 
              className="group relative"
              onMouseEnter={() => setHoveredCard('feature4')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm hover:bg-slate-800/70 transition-all duration-300 p-8 h-full transform hover:scale-105 cursor-pointer">
                <CardContent className="p-0">
                  <Sparkles className={`w-12 h-12 text-emerald-400 mb-4 transition-all duration-300 ${hoveredCard === 'feature4' ? 'scale-125 rotate-12' : ''}`} />
                  <h3 className="text-2xl font-semibold mb-4 text-white">Bias-Free Assessment</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Focus purely on skills and capabilities. Our AI evaluates performance objectively, eliminating unconscious bias from the hiring process.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 px-6 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-50"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-light tracking-tight mb-6 text-white">Pricing plans</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">Choose the plan that fits your hiring needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Starter Plan */}
            <Card className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all duration-300 flex flex-col h-full backdrop-blur-sm">
              <CardContent className="p-8 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Starter</h3>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold text-white">€5,988</span>
                    <span className="text-slate-400">/year</span>
                  </div>
                  <p className="text-sm text-slate-400">or €499/month</p>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Up to 8 positions per year with unlimited candidates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Library with 300+ AI Simulations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Build unlimited AI Simulations with Simulation builder</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">5 admin seats for recruiters and hiring managers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">3 Custom built Simulations</span>
                  </li>
                </ul>

                <Button 
                  onClick={handleBookDemo}
                  className="w-full bg-slate-700 hover:bg-slate-600 text-white py-6 text-lg rounded-lg transition-colors duration-200"
                >
                  Book a demo
                </Button>
              </CardContent>
            </Card>

            {/* Pro Plan - Featured */}
            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border-2 border-emerald-500 hover:border-emerald-400 transition-all duration-300 flex flex-col h-full relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                POPULAR
              </div>
              <CardContent className="p-8 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Pro</h3>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold text-white">€23,988</span>
                    <span className="text-slate-400">/year</span>
                  </div>
                  <p className="text-sm text-slate-400">or €1,999/month</p>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Up to 25 positions per year with unlimited candidates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Library with 300+ AI Simulations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Build unlimited AI Simulations with Simulation builder</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Unlimited admin seats for recruiters and hiring managers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">8 Custom built Simulations by Axone</span>
                  </li>
                </ul>

                <Button 
                  onClick={handleBookDemo}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-lg rounded-lg transition-colors duration-200"
                >
                  Book a demo
                </Button>
              </CardContent>
            </Card>

            {/* Enterprise Plan */}
            <Card className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all duration-300 flex flex-col h-full backdrop-blur-sm">
              <CardContent className="p-8 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Enterprise</h3>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold text-white">Custom</span>
                  </div>
                  <p className="text-sm text-slate-400">Tailored to your needs</p>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Unlimited positions and candidates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Full library access + custom simulations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Dedicated account manager</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Priority support & training</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">Custom integrations & API access</span>
                  </li>
                </ul>

                <Button 
                  onClick={handleBookDemo}
                  className="w-full bg-slate-700 hover:bg-slate-600 text-white py-6 text-lg rounded-lg transition-colors duration-200"
                >
                  Book a demo
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-400">All plans include access to our AI-powered simulation platform. No hidden fees.</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light tracking-tight mb-6 text-white">Trusted by Modern HR Teams</h2>
            <p className="text-xl text-slate-300">Join innovative organizations transforming their hiring process</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "Axone has completely transformed how we assess candidates. The realistic simulations reveal insights traditional interviews never could.",
                name: "Sarah Mitchell",
                title: "HR Director",
                image: "https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=100&h=100&fit=crop&q=40"
              },
              {
                quote: "The depth of insights we get from these AI simulations is remarkable. We're making better hiring decisions and saving significant time.",
                name: "Jessica Chen",
                title: "Talent Acquisition Lead",
                image: "https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=100&h=100&fit=crop&q=40"
              },
              {
                quote: "Finally, an assessment tool that goes beyond surface-level evaluation. Axone helps us identify true potential in candidates.",
                name: "Michael Torres",
                title: "VP of People Operations",
                image: "https://images.unsplash.com/photo-1576558656222-ba66febe3dec?w=100&h=100&fit=crop&q=40"
              }
            ].map((testimonial, i) => (
              <Card key={i} className="bg-slate-900 border-slate-800 p-8 hover:border-emerald-500/50 transition-all duration-300 flex flex-col h-full">
                <CardContent className="p-0 flex flex-col h-full">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Sparkles key={j} className="w-4 h-4 text-emerald-400 fill-emerald-400" />
                    ))}
                  </div>
                  <p className="text-slate-200 mb-6 leading-relaxed flex-grow">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center mt-auto">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4 object-cover"
                    />
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-slate-300">{testimonial.title}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-900 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-6 md:mb-0">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold tracking-tight">Axone</span>
            </div>
            
            <div className="flex items-center space-x-8 text-sm text-slate-400">
              <button className="hover:text-emerald-400 transition-colors duration-200">Privacy Policy</button>
              <button className="hover:text-emerald-400 transition-colors duration-200">Terms of Service</button>
              <button onClick={handleContact} className="hover:text-emerald-400 transition-colors duration-200">Contact</button>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-900 text-center">
            <p className="text-sm text-slate-500 mb-2">
              © 2025 Axone. All rights reserved. Redefining hiring through intelligent simulations.
            </p>
            <p className="text-sm text-slate-400">
              Contact us at{' '}
              <a 
                href="mailto:info@carreerai.io" 
                className="text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                info@carreerai.io
              </a>
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes pulse-subtle {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.3), 0 4px 6px -4px rgba(16, 185, 129, 0.3);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 20px 25px -5px rgba(16, 185, 129, 0.5), 0 8px 10px -6px rgba(16, 185, 129, 0.5);
          }
        }
        
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
        
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>

      {/* Book Demo Modal */}
      <BookDemoModal 
        isOpen={showBookDemoModal} 
        onClose={handleCloseModal} 
      />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={showContactModal} 
        onClose={handleCloseContactModal} 
      />

      <UserbackWidget />
    </div>
  );
};
